/**
 * WP-V2-PK — czysta logika sync graczy/NPC z snapshotu Colyseus.
 * Modeluje lifecycle onAdd / onChange / onRemove bez zmian protokołu.
 * Napęd animacji chodu: prędkość z próbek pozycji (interp).
 */
import type { ParkPlayerPublic, ParkNpcPublic } from '@pieskowo/shared';
import type { PosSample } from '../../net/index.js';

/** Zamrożony kontrakt V1 — zero nowych pól (test refleksyjny). */
export const FROZEN_PLAYER_FIELDS = [
  'sessionId',
  'playerId',
  'dogName',
  'breed',
  'color',
  'x',
  'y',
  'tx',
  'ty',
  'anim',
  'emote',
  'outfit',
  'connected',
] as const;

export const FROZEN_NPC_FIELDS = [
  'npcId',
  'x',
  'y',
  'anim',
  'emote',
] as const;

export const FROZEN_BALL_FIELDS = ['x', 'y', 'vx', 'vy'] as const;

export const FROZEN_PARK_STATE_KEYS = ['players', 'npcs', 'ball'] as const;

export type PlayerDiff = {
  added: string[];
  changed: string[];
  removed: string[];
  /** Wszyscy zdalni w snapshotie (bez lokalnego). */
  remoteIds: string[];
};

/**
 * Diff mapy graczy vs poprzedni zbiór id — odpowiednik
 * MapSchema onAdd / onChange / onRemove po stronie klienta sceny.
 */
export function diffPlayers(
  prevIds: ReadonlySet<string>,
  players: readonly Pick<ParkPlayerPublic, 'sessionId'>[],
  localSessionId: string | null | undefined,
): PlayerDiff {
  const remoteIds: string[] = [];
  const added: string[] = [];
  const changed: string[] = [];
  const seen = new Set<string>();

  for (const p of players) {
    if (!p.sessionId || p.sessionId === localSessionId) continue;
    remoteIds.push(p.sessionId);
    seen.add(p.sessionId);
    if (prevIds.has(p.sessionId)) {
      changed.push(p.sessionId);
    } else {
      added.push(p.sessionId);
    }
  }

  const removed: string[] = [];
  for (const id of prevIds) {
    if (!seen.has(id)) removed.push(id);
  }

  return { added, changed, removed, remoteIds };
}

export function diffNpcs(
  prevIds: ReadonlySet<string>,
  npcs: readonly Pick<ParkNpcPublic, 'npcId'>[],
): PlayerDiff {
  const remoteIds: string[] = [];
  const added: string[] = [];
  const changed: string[] = [];
  const seen = new Set<string>();

  for (const n of npcs) {
    if (!n.npcId) continue;
    remoteIds.push(n.npcId);
    seen.add(n.npcId);
    if (prevIds.has(n.npcId)) changed.push(n.npcId);
    else added.push(n.npcId);
  }

  const removed: string[] = [];
  for (const id of prevIds) {
    if (!seen.has(id)) removed.push(id);
  }

  return { added, changed, removed, remoteIds };
}

/**
 * Prędkość px/s z dwóch pozycji (do setVelocity → animacja chodu).
 * Gdy stoi — (0,0); próg ruchu 2 px.
 */
export function velocityBetween(
  from: { x: number; y: number },
  to: { x: number; y: number },
  dtSec: number,
): { vx: number; vy: number } {
  const dt = Math.max(1e-4, dtSec);
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  if (Math.hypot(dx, dy) < 2) return { vx: 0, vy: 0 };
  return { vx: dx / dt, vy: dy / dt };
}

/**
 * Prędkość z bufora próbek wokół renderTime (dla remote interp).
 * Bierze dwie sąsiednie próbki i dzieli deltę przez Δt.
 */
export function velocityFromSamples(
  samples: readonly PosSample[],
  renderTime: number,
): { vx: number; vy: number } {
  if (samples.length < 2) return { vx: 0, vy: 0 };

  // znajdź segment obejmujący renderTime (jak interpolateAt)
  let a = samples[0]!;
  let b = samples[1]!;
  for (let i = 0; i < samples.length - 1; i++) {
    const s0 = samples[i]!;
    const s1 = samples[i + 1]!;
    if (renderTime >= s0.t && renderTime <= s1.t) {
      a = s0;
      b = s1;
      break;
    }
    if (renderTime > s1.t) {
      a = s0;
      b = s1;
    }
  }
  const dtSec = Math.max(1e-3, (b.t - a.t) / 1000);
  return velocityBetween(a, b, dtSec);
}

/** Czy anim serwera sugeruje chód (walk / jump). */
export function shouldPlayWalk(anim: string | undefined | null): boolean {
  return anim === 'walk' || anim === 'jump';
}

/**
 * Asercja refleksyjna: obiekt ma wyłącznie zamrożone klucze (i wszystkie wymagane).
 * Zwraca listę problemów (pusta = OK).
 */
export function schemaFieldIssues(
  obj: Record<string, unknown>,
  frozen: readonly string[],
): string[] {
  const issues: string[] = [];
  const keys = Object.keys(obj);
  for (const k of keys) {
    if (!frozen.includes(k)) {
      issues.push(`extra:${k}`);
    }
  }
  for (const f of frozen) {
    if (!(f in obj)) {
      issues.push(`missing:${f}`);
    }
  }
  return issues;
}

/** Buduje „publiczny" snapshot gracza tylko z dozwolonych pól (test). */
export function pickPlayerPublic(
  p: ParkPlayerPublic,
): Record<(typeof FROZEN_PLAYER_FIELDS)[number], unknown> {
  return {
    sessionId: p.sessionId,
    playerId: p.playerId,
    dogName: p.dogName,
    breed: p.breed,
    color: p.color,
    x: p.x,
    y: p.y,
    tx: p.tx,
    ty: p.ty,
    anim: p.anim,
    emote: p.emote,
    outfit: p.outfit,
    connected: p.connected,
  };
}
